<template>
  <Card :title="title" :subtitle="subtitle">
    <div class="hint">
      <Spinner />
      <span class="hint-text">{{ hintText }}</span>
    </div>

    <div class="countdown" :class="{ warn: remaining <= 10, expired: remaining === 0 }">
      <div class="countdown-label">剩餘時間</div>
      <div class="countdown-value">
        <template v-if="remaining > 0">{{ remaining }} 秒</template>
        <template v-else>已逾時,等待系統確認…</template>
      </div>
      <div class="countdown-bar">
        <div class="countdown-bar-fill" :style="{ width: percent + '%' }" />
      </div>
    </div>

    <div class="meta">
      <div class="meta-row">
        <span class="meta-label">販賣機編號</span>
        <span class="meta-value">{{ posMachineCode }}</span>
      </div>
    </div>
    <template #footer>
      <button class="btn btn-ghost" :disabled="cancelling" @click="$emit('cancel')">
        取消交易
      </button>
    </template>
  </Card>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import Card from './Card.vue'
import Spinner from './Spinner.vue'

const props = withDefaults(
  defineProps<{
    posMachineCode: string
    cancelling?: boolean
    /** 倒數起算時間 (ms) */
    startedAt: number
    /** 倒數秒數 */
    timeoutSeconds?: number
    title?: string
    subtitle?: string
    hintText?: string
  }>(),
  {
    timeoutSeconds: 60,
    title: '請於販賣機按下您要購買的商品',
    subtitle: '購買完畢後,系統會自動進入下一步',
    hintText: '等待販賣機通知中...',
  },
)

defineEmits<{ (e: 'cancel'): void }>()

const now = ref(Date.now())
let timer: number | null = null

function tick() {
  now.value = Date.now()
}

function start() {
  stop()
  tick()
  timer = window.setInterval(tick, 1000)
}

function stop() {
  if (timer != null) {
    window.clearInterval(timer)
    timer = null
  }
}

const remaining = computed(() => {
  const elapsed = Math.floor((now.value - props.startedAt) / 1000)
  return Math.max(0, props.timeoutSeconds - elapsed)
})

const percent = computed(() => (remaining.value / props.timeoutSeconds) * 100)

watch(() => props.startedAt, start)
onMounted(start)
onBeforeUnmount(stop)
</script>

<style scoped>
.hint {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #ff6b35;
  font-weight: 500;
  margin: 8px 0 16px;
}
.hint-text {
  font-size: 15px;
}
.countdown {
  background: #fff7ed;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.countdown-label {
  font-size: 12px;
  color: #9a3412;
}
.countdown-value {
  font-size: 22px;
  font-weight: 700;
  color: #ea580c;
  margin: 2px 0 8px;
  font-variant-numeric: tabular-nums;
}
.countdown-bar {
  height: 6px;
  background: #fed7aa;
  border-radius: 999px;
  overflow: hidden;
}
.countdown-bar-fill {
  height: 100%;
  background: #ea580c;
  border-radius: 999px;
  transition: width 1s linear;
}
.countdown.warn .countdown-value {
  color: #b91c1c;
}
.countdown.warn .countdown-bar-fill {
  background: #b91c1c;
}
.countdown.expired .countdown-value {
  font-size: 15px;
  font-weight: 500;
}
.meta {
  background: #f9fafb;
  border-radius: 12px;
  padding: 12px 14px;
}
.meta-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}
.meta-label {
  color: #6b7280;
}
.meta-value {
  color: #111827;
  font-weight: 500;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
}
</style>
